import React, { useEffect, useCallback } from 'react'
import { Button, Space } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { checkIfAnyDropdownIsOpen, checkIfAnyModalIsOpen } from '../../../../utils/antd.helper'
import useClusterGroups from '../cluster/useClusterGroups'

const FileDrawerNavigation = () => {
    const drawer = useSelector(state => state.assignment.drawer)
    const { clusters: filteredClusters } = useClusterGroups({ applyFilter: true })
    const dispatch = useDispatch()

    const handleKeyDown = useCallback(event => {
        if (checkIfAnyModalIsOpen() || checkIfAnyDropdownIsOpen()) return
        if (_.isEmpty(drawer.file)) return
        if (['INPUT', 'TEXTAREA'].includes(document.activeElement.nodeName)) return // To not trigger when wanting to move between characters in input and not files
        if (document.activeElement.className.includes('DragHandle')) return
        let fileIdToScrollTo = null
        if (event.key === 'ArrowLeft') {
            handleArrowLeftClick()
        } else if (event.key === 'ArrowRight') {
            handleArrowRightClick()
        } else if (event.key === 'ArrowDown') {
            fileIdToScrollTo = handleArrowDownClick()
        } else if (event.key === 'ArrowUp') {
            fileIdToScrollTo = handleArrowUpClick()
        }
        if (fileIdToScrollTo) {
            document.getElementById(`cluster__file-card-${fileIdToScrollTo}`).scrollIntoView({ block: 'center' })
        }
    }, [filteredClusters, drawer, dispatch])

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [handleKeyDown])

    const handleArrowRightClick = () => {
        const currentCluster = getCurrentCluster()
        const currentDrawerFileIndex = getCurrentDrawerFileIndex(currentCluster)
        if (currentCluster.files[currentDrawerFileIndex + 1]) {
            dispatch(actions.setDrawerFile(currentCluster.files[currentDrawerFileIndex + 1]))
        }
    }

    const handleArrowLeftClick = () => {
        const currentCluster = getCurrentCluster()
        const currentDrawerFileIndex = getCurrentDrawerFileIndex(currentCluster)
        if (currentCluster.files[currentDrawerFileIndex - 1]) {
            dispatch(actions.setDrawerFile(currentCluster.files[currentDrawerFileIndex - 1]))
        }
    }

    const handleArrowDownClick = () => {
        const nextCluster = getNextCluster()
        if (nextCluster) {
            dispatch(actions.setDrawerFile(nextCluster.files[0]))
            return nextCluster.files[0].id
        }
    }

    const handleArrowUpClick = () => {
        const previousCluster = getPreviousCluster()
        if (previousCluster) {
            dispatch(actions.setDrawerFile(previousCluster.files[0]))
            return previousCluster.files[0].id
        }
    }

    const getCurrentCluster = () => {
        return filteredClusters.find(cluster =>
            cluster.files.some(file => Number(file.id) === Number(drawer?.file?.id)),
        )
    }

    const getCurrentClusterIndex = () => {
        return filteredClusters.findIndex(cluster =>
            cluster.files.some(file => Number(file.id) === Number(drawer?.file?.id)),
        )
    }

    const getNextCluster = () => {
        const currentClusterIndex = getCurrentClusterIndex()
        let nextClusterWithFiles
        for (let i = currentClusterIndex + 1; i < filteredClusters.length; i++) {
            if (filteredClusters[i].files.length) {
                nextClusterWithFiles = filteredClusters[i]
                break
            }
        }
        return nextClusterWithFiles
    }

    const getPreviousCluster = () => {
        const currentClusterIndex = getCurrentClusterIndex()
        let prevClusterWithFiles
        for (let i = currentClusterIndex - 1; i >= 0; i--) {
            if (filteredClusters[i].files.length) {
                prevClusterWithFiles = filteredClusters[i]
                break
            }
        }
        return prevClusterWithFiles
    }

    const getCurrentDrawerFileIndex = currentCluster =>
        currentCluster.files.findIndex(file => Number(file.id) === Number(drawer?.file?.id))

    return (
        <Space size={8}>
            <Button shape='circle' icon={<LeftOutlined />} onClick={handleArrowLeftClick} aria-label='previous file' />
            <Button shape='circle' icon={<RightOutlined />} onClick={handleArrowRightClick} aria-label='next file' />
        </Space>
    )
}

export default FileDrawerNavigation
